import React, { useState } from "react";

/**
 * MultiCompleteModal
 *
 * Modes:
 * 1) autoQuantity: true  → no inputs; auto piecesFinished = qtyToMake
 * 2) autoQuantity: false → show table per job
 *
 * Props:
 * - open
 * - jobs: [{ id, jobNumber, name, qty }]
 * - operationCode
 * - autoQuantity (boolean)
 * - onCancel()
 * - onSubmit(data)
 *
 * onSubmit returns:
 *  - { type: "auto-quantity" }
 *  - { type: "table", byJob: { [jobId]: { piecesFinished, piecesScrapped } } }
 */

export default function MultiCompleteModal({
  open,
  jobs = [],
  operationCode,
  autoQuantity = false,
  onCancel,
  onSubmit,
}) {
  const [values, setValues] = useState({});

  if (!open) return null;

  const handleChange = (jobId, field, value) => {
    setValues((prev) => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        [field]: value,
      },
    }));
  };

  const handleConfirm = () => {
    if (autoQuantity) {
      onSubmit({ type: "auto-quantity" });
      return;
    }

    const byJob = {};
    for (const job of jobs) {
      const v = values[job.id] || {};
      const finished = Number(v.piecesFinished || 0);
      const scrapped = Number(v.piecesScrapped || 0);
      byJob[job.id] = { piecesFinished: finished, piecesScrapped: scrapped };
    }

    onSubmit({ type: "table", byJob });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-2xl flex flex-col gap-4 shadow-2xl">
        <h2 className="text-lg font-semibold text-purple-600">
          Complete Multiple Jobs — {operationCode}
        </h2>

        {autoQuantity ? (
          <div className="text-gray-700 text-sm">
            You completed <span className="font-semibold text-gray-900">{operationCode}</span>{" "}
            for {jobs.length} jobs.
            <br />
            The system will apply{" "}
            <span className="text-green-600 font-semibold">
              Quantity-To-Make
            </span>{" "}
            for each job automatically, with zero scrapped pieces.
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm text-gray-700">
              <thead className="bg-gray-100 text-gray-800 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Job #</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-center">Finished</th>
                  <th className="px-3 py-2 text-center">Scrapped</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {jobs.map((job) => {
                  const v = values[job.id] || {};
                  return (
                    <tr key={job.id} className="border-t border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-gray-900">{job.jobNumber}</td>
                      <td className="px-3 py-2 text-gray-700">{job.name}</td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="number"
                          min="0"
                          value={v.piecesFinished || ""}
                          onChange={(e) =>
                            handleChange(job.id, "piecesFinished", e.target.value)
                          }
                          className="w-20 bg-white text-gray-900 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="number"
                          min="0"
                          value={v.piecesScrapped || ""}
                          onChange={(e) =>
                            handleChange(job.id, "piecesScrapped", e.target.value)
                          }
                          className="w-20 bg-white text-gray-900 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 transition-colors"
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            Confirm Completion
          </button>
        </div>
      </div>
    </div>
  );
}
